import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { INTEGRATION_LOGS } from '../../utils/constants'
import { SvgDropdownArrowGrey, SvgWarningNew } from '@ui-kit'
import { getGenericEntityListingAPI, hideLoader, showLoader } from '@app-store'
import { FieldValues } from 'react-hook-form'
import { useAppDispatch, useAppSelector } from '../../utils/agent.store'
import IntegrationLogsTable from '../../components/integration-logs/IntegrationLogsTable'
import { IntegrationType } from '../../types/types'
import { canViewCentralizedIntegrationLogs } from '../../utils/permission'

const IntegrationLogsPage: React.FC = () => {
  const selectedIntegrationRef = useRef<IntegrationType>()
  const [selectedIntegrationKey, setSelectedIntegrationKey] = useState<string>()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [integrationList, setIntegrationList] = useState<IntegrationType[]>()
  const ref = useRef<HTMLDivElement>(null)
  const dispatch = useAppDispatch()
  const [hasPermission, setHasPermission] = useState(false)
  const selectedIntegration = selectedIntegrationRef.current
  const programPermission = useAppSelector(
    (state) => state.clientPermissions.programPermissions
  )

  const handleSelectIntegration = useCallback(
    (integration: IntegrationType) => {
      selectedIntegrationRef.current = integration
      setSelectedIntegrationKey(integration.value)
      setIsDropdownOpen(false)
    },
    []
  )

  const fetchIntegrationList = useCallback(async () => {
    dispatch(showLoader())

    try {
      const res = await getGenericEntityListingAPI({
        select: ['identifier', 'integration_name', 'group_id', 'request_type'],
        entity_name: 'integration_functionality',
      })

      if (res) {
        const arr: IntegrationType[] = res
          .map((item: FieldValues) => ({
            label: item.integration_name,
            value: item.group_id,
            identifier: item.identifier,
            request_type: item.request_type,
          }))
          .filter(
            (item: IntegrationType, index: number, self: IntegrationType[]) =>
              index === self.findIndex((t) => t.value === item.value)
          )

        setIntegrationList(arr)

        if (arr.length > 0) {
          selectedIntegrationRef.current = arr[0]
          setSelectedIntegrationKey(arr[0].value)
        }
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }, [dispatch])

  useEffect(() => {
    fetchIntegrationList()
  }, [fetchIntegrationList])

  useEffect(() => {
    if (programPermission) {
      const permission = canViewCentralizedIntegrationLogs()
      setHasPermission(permission)
    }
  }, [programPermission])

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', onDoc)

    return () => {
      document.removeEventListener('mousedown', onDoc)
    }
  }, [])

  if (!hasPermission) {
    return (
      <div className="p-6 h-full flex flex-col">
        <div className="flex flex-col justify-center items-center gap-[1.5rem] h-[25.75rem] mt-8.5 mx-0 border border-accent-100 rounded-lg relative">
          <div>
            <SvgWarningNew />
          </div>
          <div className="font-light text-xs">
            {INTEGRATION_LOGS.NO_PERMISSION_MESSAGE}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="flex flex-row gap-2 items-center">
        <span className="text-sm font-medium text-accent-900">
          {INTEGRATION_LOGS.INTEGRATION}
        </span>

        <span className="text-sm font-medium text-accent-300">|</span>

        <div className="relative" ref={ref}>
          <div
            className="flex flex-row gap-2 items-center cursor-pointer hover:bg-accent-50 rounded px-0.5 -mx-0.5"
            onClick={() => setIsDropdownOpen((v) => !v)}
          >
            <span className="text-secondary-500 text-sm font-medium">
              {selectedIntegration?.label ?? ''}
            </span>

            <SvgDropdownArrowGrey
              fill="#0A6FCD"
              className={`transition-transform ${
                isDropdownOpen ? 'rotate-180' : ''
              }`}
            />
          </div>

          {isDropdownOpen && (
            <div className="absolute left-0 top-full z-50 mt-1 min-w-[16rem] rounded border border-accent-200 bg-white shadow-md divide-y divide-accent-100">
              {integrationList?.map((opt) => (
                <div
                  key={opt.value}
                  className={`px-3 py-2 text-sm font-medium cursor-pointer ${
                    selectedIntegration?.value === opt.value
                      ? 'bg-secondary-50 text-secondary-500'
                      : 'text-accent-900 hover:bg-accent-50'
                  }`}
                  onClick={() => handleSelectIntegration(opt)}
                >
                  {opt.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col pt-3">
        {selectedIntegration && (
          <IntegrationLogsTable
            key={selectedIntegrationKey}
            selectedIntegration={selectedIntegration}
          />
        )}
      </div>
    </div>
  )
}

export default memo(IntegrationLogsPage)
